import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, UsersThree, LinkSimple, Copy, CircleNotch, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { copyToClipboard } from "@/lib/clipboard";

export function TeamModal({ open, onClose }) {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [team, setTeam] = useState(null);

  const inviteLink = team ? `${window.location.origin}/workspace?invite=${team.invite_code}` : "";

  const createOrLoad = async () => {
    if (!email.trim() || !email.includes("@")) { toast.error("Enter a valid email."); return; }
    setLoading(true);
    try {
      const r = await api.post("/team/create", { owner_email: email.trim() });
      setTeam(r.data.team);
      toast.success(r.data.created ? "Team created." : "Team loaded.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create team. A Firm subscription is required.");
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    const ok = await copyToClipboard(inviteLink);
    if (ok) toast.success("Invite link copied.");
    else toast.error("Could not copy — select and copy the link manually.");
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="team-modal">
          <div className="absolute inset-0 bg-lc-navy/70 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ scale: 0.95, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, opacity: 0 }}
            transition={{ type: "spring", stiffness: 250, damping: 24 }}
            className="relative w-full max-w-lg rounded-sm border border-lc-ink/10 bg-white p-7 shadow-2xl"
          >
            <button onClick={onClose} data-testid="team-close"
              className="absolute right-4 top-4 text-lc-steel hover:text-lc-ink transition-colors duration-200">
              <X size={18} />
            </button>
            <div className="flex h-11 w-11 items-center justify-center rounded-sm bg-lc-navy">
              <UsersThree size={22} weight="duotone" className="text-lc-sky" />
            </div>
            <h2 className="mt-5 font-serif text-2xl text-lc-ink">{t("team.title")}</h2>
            <p className="mt-2 text-sm text-lc-steel leading-relaxed">{t("team.sub")}</p>

            <div className="mt-6">
              <label className="text-xs font-semibold text-lc-steel">{t("team.ownerEmail")}</label>
              <div className="mt-2 flex gap-2">
                <input
                  data-testid="team-owner-email" type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)} placeholder="owner@firm.com"
                  className="flex-1 rounded-sm border border-lc-ink/15 bg-lc-paper px-3 py-2 text-sm outline-none focus:border-lc-ocean focus:ring-2 focus:ring-lc-ocean/20 transition-colors duration-200"
                />
                <button data-testid="team-create-btn" onClick={createOrLoad} disabled={loading}
                  className="inline-flex items-center gap-2 rounded-sm bg-lc-ocean px-4 py-2 text-sm font-semibold text-white hover:bg-lc-navy transition-colors duration-200 disabled:opacity-60">
                  {loading ? <CircleNotch size={15} className="animate-spin" /> : <UsersThree size={15} weight="bold" />}
                  {t("team.create")}
                </button>
              </div>
            </div>

            {team && (
              <div className="mt-6 border-t border-lc-ink/10 pt-5" data-testid="team-details">
                <label className="text-xs font-semibold text-lc-steel">{t("team.inviteLink")}</label>
                <div className="mt-2 flex gap-2">
                  <div className="flex flex-1 items-center gap-2 rounded-sm border border-lc-ink/15 bg-lc-paper px-3 py-2">
                    <LinkSimple size={15} className="shrink-0 text-lc-ocean" />
                    <span data-testid="team-invite-link" className="truncate font-mono text-[11px] text-lc-ink">{inviteLink}</span>
                  </div>
                  <button data-testid="team-copy-btn" onClick={copyLink}
                    className="inline-flex items-center gap-1.5 rounded-sm bg-lc-navy px-3 py-2 text-xs font-semibold text-white hover:bg-lc-ocean transition-colors duration-200">
                    <Copy size={14} /> {t("team.copy")}
                  </button>
                </div>
                <div className="mt-2 font-mono text-[11px] text-lc-steel">
                  Invite code: <span className="text-lc-ink">{team.invite_code}</span>
                </div>

                <div className="mt-5 flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-lc-steel">{t("team.members")}</span>
                  <span className="font-mono text-[11px] text-lc-ocean" data-testid="team-seats">
                    {team.members.length} / {team.seats_limit} {t("team.seatsUsed")}
                  </span>
                </div>
                <div className="mt-2 space-y-1.5">
                  {team.members.map((m, i) => (
                    <div key={m} className="flex items-center gap-2 rounded-sm bg-lc-paper px-3 py-2 text-xs text-lc-ink">
                      <CheckCircle size={13} weight="fill" className="text-lc-ok" />
                      {m} {i === 0 && <span className="font-mono text-[9px] uppercase text-lc-steel">· owner</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
